import { useMutation } from '@apollo/client/react';
import { type FC, type ReactNode } from 'react';

import { Form, FormButtons, FormInput } from '@zen/Components';
import type { FormInstance } from '@zen/Components/types';
import { Button } from '@zen/DesignSystem';
import { useNotification } from '@zen/utils/hooks/useNotification';
import type { IOkOrErrorResult } from '@zen/utils/OkOrErrorResult';
import { performFormMutation } from '@zen/utils/performMutation';

import {
  UpdateAccountFinanceIdDocument,
  type UpdateAccountFinanceIdMutationResult,
  type UpdateAccountFinanceIdMutationVariables
} from '../graphql';
import type { UpdateAccountFinanceIdInput } from '../types';
import { updateAccountFinancialDetailsFormSchema } from './updateAccountFinancialDetailsForm.validation';

export type UpdateAccountFinancialsFormInitialValues = UpdateAccountFinanceIdMutationVariables['input'];

interface Props {
  initialValues: UpdateAccountFinancialsFormInitialValues;
  onCancel?: () => void;
  onSuccess?: () => void;
}

const UpdateAccountFinancialsForm: FC<Props> = ({ initialValues, onCancel, onSuccess }) => {
  const [updateAccountFinancials] = useMutation<UpdateAccountFinanceIdMutationResult, UpdateAccountFinanceIdMutationVariables>(
    UpdateAccountFinanceIdDocument,
    { refetchQueries: ['accountFinancials'] }
  );
  const { addError, addSuccess } = useNotification();

  const handleUpdateAccountFinancials = async (values: UpdateAccountFinanceIdInput): Promise<IOkOrErrorResult> => {
    return performFormMutation({
      mutationFn: () =>
        updateAccountFinancials({
          variables: {
            input: values
          }
        }),
      onError: () => addError(),
      onSuccess: () => addSuccess('Accounts financial details updated.')
    });
  };

  const renderFormButtons = ({ isSubmitting }: FormInstance<UpdateAccountFinancialsFormInitialValues>): ReactNode => (
    <FormButtons isSubmitting={isSubmitting} layout="fixed" text="Save changes">
      <Button data-testid="cancel-button" onClick={onCancel} variant="ghost">
        Cancel
      </Button>
    </FormButtons>
  );

  return (
    <Form
      formButtons={renderFormButtons}
      initialValues={initialValues}
      onSubmit={handleUpdateAccountFinancials}
      validationSchema={updateAccountFinancialDetailsFormSchema}
      onSuccess={onSuccess}
    >
      {({ values }: FormInstance<UpdateAccountFinancialsFormInitialValues>) => {
        return <FormInput label="Finance identifier" name="financeId" />;
      }}
    </Form>
  );
};

export default UpdateAccountFinancialsForm;
